/* Header menus
 *
 * <nav-disclosure> replaces the theme's <hover-disclosure> on the desktop
 * menu. It keeps the hover behaviour and the meganav classes the theme CSS
 * already styles, and adds a real toggle button: aria-expanded, Escape to
 * close (focus returns to the toggle) and closing when focus leaves the item.
 *
 * <nav-accordion> opens and closes a mobile menu item's children in place.
 */
if (!customElements.get('nav-disclosure')) {
  customElements.define(
    'nav-disclosure',
    class NavDisclosure extends HTMLElement {
      connectedCallback() {
        this.wrapper = this.closest('[data-header-wrapper]');
        this.link = this.querySelector('[data-top-link]');
        this.toggle = this.querySelector('[data-dropdown-toggle]');
        this.dropdown = document.getElementById(this.dataset.dropdown);
        this.grandparent = this.classList.contains('grandparent');
        this.transitionTimeout = 0;
        this.lastPointerType = '';
        this.wasOpenOnPointerDown = false;

        if (!this.dropdown) return;

        this.staggerChildAnimations();

        this.addEventListener('mouseenter', (event) => this.show(event));
        this.addEventListener('mouseleave', () => this.hide());

        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.hide();
        });

        this.addEventListener('keydown', (event) => {
          if (event.code !== 'Escape' || !this.isOpen) return;
          event.stopPropagation();
          this.hide();
          if (this.toggle) this.toggle.focus();
        });

        if (this.toggle) {
          this.toggle.addEventListener('click', (event) => {
            event.preventDefault();
            if (this.isOpen) {
              this.hide();
            } else {
              this.show(event);
            }
          });
        }

        // Tablets: the first tap on the top link opens the dropdown, the
        // second follows the link. The open state is read on pointerdown
        // because the emulated mouseenter opens it before the click lands.
        if (this.link) {
          this.link.addEventListener('pointerdown', (event) => {
            this.lastPointerType = event.pointerType;
            this.wasOpenOnPointerDown = this.isOpen;
          });
          this.link.addEventListener('click', (event) => {
            if (this.lastPointerType === 'touch' && !this.wasOpenOnPointerDown) {
              event.preventDefault();
              this.show(event);
            }
          });
        }
      }

      get isOpen() {
        return this.classList.contains('is-visible');
      }

      show(event) {
        document.querySelectorAll('nav-disclosure.is-visible').forEach((item) => {
          if (item !== this) item.hide();
        });

        if (this.wrapper) {
          if (event && event.type === 'mouseenter') {
            this.wrapper.classList.add('meganav--is-transitioning');
          }
          this.wrapper.classList.toggle('meganav--visible', this.grandparent);
        }

        this.classList.add('is-visible');
        this.dropdown.classList.add('is-visible');
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');

        clearTimeout(this.transitionTimeout);
        this.transitionTimeout = setTimeout(() => {
          if (this.wrapper) this.wrapper.classList.remove('meganav--is-transitioning');
        }, 200);
      }

      hide() {
        if (!this.isOpen) return;

        this.classList.remove('is-visible');
        this.dropdown.classList.remove('is-visible');
        if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');

        if (this.wrapper && !document.querySelector('nav-disclosure.is-visible')) {
          this.wrapper.classList.remove('meganav--visible', 'meganav--is-transitioning');
        }
      }

      // Same timing as the theme's hover-disclosure.
      staggerChildAnimations() {
        let step = 50;
        this.querySelectorAll('[data-stagger]').forEach((element, index) => {
          element.style.transitionDelay = `${index * step + 10}ms`;
          step *= 0.95;
        });

        this.querySelectorAll('[data-stagger-first]').forEach((child, i) => {
          const delay = i * 100;
          child.style.transitionDelay = `${delay}ms`;
          child.parentElement.querySelectorAll('[data-stagger-second]').forEach((grandchild, i2) => {
            grandchild.style.transitionDelay = `${delay + (i2 + 1) * 20}ms`;
          });
        });
      }
    }
  );
}

if (!customElements.get('nav-accordion')) {
  customElements.define(
    'nav-accordion',
    class NavAccordion extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector(':scope > .nav-accordion__row > [data-accordion-toggle]');
        this.panel = this.toggle && document.getElementById(this.toggle.getAttribute('aria-controls'));
        if (!this.toggle || !this.panel) return;

        this.toggle.addEventListener('click', () => {
          this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true');
        });
      }

      setOpen(open) {
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.panel.hidden = !open;
        this.classList.toggle('is-open', open);
      }
    }
  );
}
